import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { logoUrlFromBranding, resolveClinicLogoUrl } from '@/lib/clinicBranding';
import type { Json } from '@/types/database.generated';

export const CLINIC_LOGOS_QUERY_KEY = 'clinic-logos' as const;
export const CLINIC_LOGOS_STALE_MS = 5 * 60_000;

export function clinicLogosQueryKey(clinicIds: readonly string[]) {
  return [CLINIC_LOGOS_QUERY_KEY, ...clinicIds] as const;
}

function uniqueSortedIds(clinicIds: readonly string[]): string[] {
  return [...new Set(clinicIds.filter(Boolean))].sort();
}

/**
 * Resolves branding logos for clinics the caller can already see (RLS:
 * `clinics_select_member_or_admin`). Uses the public `clinic-logos` URL
 * helper already used by the portal and prints.
 */
export function useClinicLogos(clinicIds: readonly string[]) {
  const idKey = uniqueSortedIds(clinicIds).join(',');
  const ids = useMemo(() => (idKey.length === 0 ? [] : idKey.split(',')), [idKey]);

  return useQuery({
    queryKey: clinicLogosQueryKey(ids),
    enabled: ids.length > 0,
    staleTime: CLINIC_LOGOS_STALE_MS,
    queryFn: async (): Promise<Record<string, string | null>> => {
      const logos: Record<string, string | null> = {};
      for (const id of ids) logos[id] = null;

      const { data, error } = await supabase.from('clinics').select('id, branding').in('id', ids);
      if (error) throw error;

      for (const row of data ?? []) {
        logos[row.id] = resolveClinicLogoUrl(logoUrlFromBranding(row.branding as Json));
      }
      return logos;
    },
  });
}
